import React, { Fragment } from 'react'
import { withRouter } from 'react-router'

import { useUnit } from 'effector-react'

import { useActions, useEffect, useMediaQueries, useSelector } from 'hooks'

import { goTo, urlTo } from 'helpers/router'

import { fetchClient as fetchClientActionCreator } from 'app/redux/actions/client'

import Inner from 'components/_old/Inner/Inner.jsx'
import { Typo } from 'components/_old/Typo/Typo'

import { GatewayDest } from 'components/atoms/Gateway'
import { DesktopLayout, MobileLayout, MobileLayoutFooterButton } from 'components/atoms/Layouts'
import { NavigationBar } from 'components/atoms/NavigationBar'
import { Typography } from 'components/atoms/Typography'

import { Preloader } from 'components/molecules/Preloader'

import { DesktopFooter } from 'app/containers/Footer'
import { DesktopHeader } from 'app/containers/Header'

import type { RouteProps } from 'app/pages/Dashboard/UserProfile/types'

import { Step } from './constants'
import { $step, $isLoading, $currentStepNumber, $currentFlowStepsCount, goBack } from './model'

type TwoFactorSetupLayoutProps = {
  isLoading?: boolean
  children: React.ReactNode
  button?: React.ReactNode
  routes: RouteProps[]
  location: {
    query: Record<string, string>
  }
}

const TwoFactorSetupLayout = withRouter(
  ({ isLoading: isStepLoading, children, routes, button, location }: TwoFactorSetupLayoutProps): React.ReactElement => {
    const { desktop } = useMediaQueries()

    const currentRouteModule = routes[routes.length - 1].module

    const isClientFetched = useSelector((state) => state.client.is_fetched)

    const [fetchClient] = useActions([fetchClientActionCreator])

    const [step, isLoading, currentStepNumber, currentFlowStepsCount, handleGoBack] = useUnit([
      $step,
      $isLoading,
      $currentStepNumber,
      $currentFlowStepsCount,
      goBack,
    ])

    useEffect(() => {
      if (step === Step.INITIAL && currentRouteModule !== 'two-factor-auth-setup') {
        goTo(urlTo('two-factor-auth-setup', {}, location.query))
      }
    }, [step, currentRouteModule])

    useEffect(() => {
      if (!isClientFetched) {
        fetchClient()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClientFetched])

    const navbarNode = (
      <NavigationBar leftPartText="Back" onLeftPartClick={currentStepNumber > 1 ? handleGoBack : undefined}>
        <Typography size={20} lineHeight="small" weight="semibold">
          <Typo>
            Step {currentStepNumber} of {currentFlowStepsCount}
          </Typo>
        </Typography>
      </NavigationBar>
    )

    return (
      <Fragment>
        {desktop ? (
          <DesktopLayout
            header={<DesktopHeader />}
            content={
              <Inner>
                {navbarNode}
                {children}
              </Inner>
            }
            footer={<DesktopFooter />}
          />
        ) : (
          <MobileLayout
            header={navbarNode}
            content={children}
            footer={<MobileLayoutFooterButton opaque>{button}</MobileLayoutFooterButton>}
          />
        )}

        <Preloader loading={isStepLoading ?? isLoading ?? !isClientFetched} size="big" zIndex />

        <GatewayDest name="toasts" />
        <GatewayDest name="modals" />
      </Fragment>
    )
  },
)

export { TwoFactorSetupLayout }
